import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { ChevronLeft, ChevronRight, X, Clock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, isToday, isBefore, startOfDay, parseISO } from 'date-fns';

// Parse date string as local date (not UTC)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  // Handle ISO strings by extracting just the date part
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Check if two dates are the same day (local comparison)
const isSameDayLocal = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

// Format time for display (convert 24h to 12h)
const formatTime12h = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export default function ContractorAvailability() {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeMode, setTimeMode] = useState('allDay'); // 'allDay' or 'partial'
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [saving, setSaving] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  useEffect(() => {
    loadAvailability();
  }, [currentMonth, user.id]);

  const loadAvailability = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/availability?startDate=${format(monthStart, 'yyyy-MM-dd')}&endDate=${format(monthEnd, 'yyyy-MM-dd')}`);
      setAvailability(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const getAvailabilityForDate = (date) => {
    return availability.find(a => isSameDayLocal(parseLocalDate(a.date), date));
  };

  const isUnavailable = (date) => {
    return !!getAvailabilityForDate(date);
  };

  const handleDayClick = (date) => {
    // Don't allow toggling past dates
    if (isBefore(date, startOfDay(new Date()))) return;

    const existing = getAvailabilityForDate(date);
    
    if (existing) {
      // Remove unavailability
      api.delete(`/availability/${existing.id}`).then(loadAvailability);
    } else {
      // Show modal to select time
      setSelectedDate(date);
      setTimeMode('allDay');
      setStartTime('09:00');
      setEndTime('17:00');
      setShowTimeModal(true);
    }
  };

  const handleSaveAvailability = async () => {
    if (!selectedDate) return;
    
    setSaving(true);
    try {
      const payload = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        allDay: timeMode === 'allDay'
      };
      
      if (timeMode === 'partial') {
        payload.startTime = startTime;
        payload.endTime = endTime;
      }
      
      await api.post('/availability', payload);
      setShowTimeModal(false);
      loadAvailability();
    } catch (err) {
      alert(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">My Availability</h1>
      </div>

      <div className="card">
        <div className="card-body">
          <p className="text-sm text-gray-600 mb-4">
            Tap a date to mark yourself <span className="text-red-600 font-medium">unavailable</span>. 
            This helps avoid getting booked when you can't work.
          </p>

          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="btn-ghost p-2">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-semibold">{format(currentMonth, 'MMMM yyyy')}</span>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="btn-ghost p-2">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}

            {/* Empty cells for days before month starts */}
            {Array.from({ length: monthStart.getDay() }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square"></div>
            ))}

            {/* Days */}
            {days.map(day => {
              const avail = getAvailabilityForDate(day);
              const unavailable = !!avail;
              const past = isBefore(day, startOfDay(new Date()));
              const today = isToday(day);
              const isPartial = avail && !avail.allDay;

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => handleDayClick(day)}
                  disabled={past}
                  className={`
                    aspect-square flex flex-col items-center justify-center rounded-lg text-sm font-medium
                    transition-colors relative
                    ${past ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'}
                    ${today ? 'ring-2 ring-hub-500 ring-offset-1' : ''}
                    ${unavailable && !past ? (isPartial ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700') : ''}
                  `}
                >
                  {format(day, 'd')}
                  {unavailable && !past && (
                    <span className={`absolute top-0.5 right-0.5 w-2 h-2 ${isPartial ? 'bg-orange-500' : 'bg-red-500'} rounded-full`}></span>
                  )}
                  {isPartial && !past && (
                    <Clock className="w-3 h-3 mt-0.5" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-600 flex-wrap">
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-white border border-gray-200"></span>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-red-100 border border-red-200"></span>
              <span>All Day Off</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-orange-100 border border-orange-200"></span>
              <span>Partial</span>
            </div>
          </div>
        </div>
      </div>

      {/* List of unavailable dates */}
      {availability.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Unavailable Dates</h2>
          </div>
          <div className="card-body divide-y divide-gray-100">
            {availability.sort((a, b) => new Date(a.date) - new Date(b.date)).map(a => {
              const dateObj = parseLocalDate(a.date);
              return (
                <div key={a.id} className="flex items-center justify-between py-2">
                  <div>
                    <span className="text-sm font-medium">{format(dateObj, 'EEEE, MMMM d, yyyy')}</span>
                    {a.allDay ? (
                      <span className="ml-2 text-xs text-red-600">(All Day)</span>
                    ) : (
                      <span className="ml-2 text-xs text-orange-600">
                        ({formatTime12h(a.startTime)} - {formatTime12h(a.endTime)})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      api.delete(`/availability/${a.id}`).then(loadAvailability);
                    }}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Time selection modal */}
      {showTimeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-slide-up">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Mark Unavailable
              </h2>
              <button onClick={() => setShowTimeModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </p>

              {/* All Day vs Partial */}
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="timeMode"
                    value="allDay"
                    checked={timeMode === 'allDay'}
                    onChange={() => setTimeMode('allDay')}
                    className="w-4 h-4 text-hub-600"
                  />
                  <div>
                    <p className="font-medium">All Day</p>
                    <p className="text-sm text-gray-500">Not available the entire day</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="timeMode"
                    value="partial"
                    checked={timeMode === 'partial'}
                    onChange={() => setTimeMode('partial')}
                    className="w-4 h-4 text-hub-600"
                  />
                  <div>
                    <p className="font-medium">Specific Hours</p>
                    <p className="text-sm text-gray-500">Only unavailable during certain times</p>
                  </div>
                </label>
              </div>

              {/* Time pickers */}
              {timeMode === 'partial' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Unavailable From</label>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Until</label>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="input"
                    />
                  </div>
                </div>
              )}

              <button
                onClick={handleSaveAvailability}
                disabled={saving}
                className="btn-primary w-full"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
